import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CreditCard, ExternalLink, Loader2, Trash2 } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useAuth } from '@/features/auth';
import { ConsentDialog } from './ConsentDialog';
import { useSubscriptionNotice } from '../hooks/useSubscriptionNotice';
import { deleteOwnAccount, recordConsent } from '../api';
import type { ConsentSource, ConsentState, ConsentType } from '../types';

interface DeclineDialogProps {
  /** Welche Einwilligungen abgelehnt bzw. widerrufen werden. */
  consentTypes: ConsentType[];
  state: ConsentState;
  source: ConsentSource;
  onCancel: () => void;
}

/**
 * Ablehnung und Widerruf - ein Weg, ein Loeschpfad.
 *
 * Die Ablehnung wird protokolliert, bevor geloescht wird: scheitert die
 * Loeschung, steht die Entscheidung trotzdem im Protokoll. Gelingt sie,
 * raeumt der FK-CASCADE auf auth.users das Protokoll mit ab - das ist so
 * gewollt und im Kopf von Migration 172 begruendet.
 *
 * Geloescht wird ueber delete_own_account() (136). Der fruehere Pfad im
 * ConsentManagementModal hat stattdessen Tabelle fuer Tabelle vom Client
 * aus geloescht, das Profil dabei wegen fehlender DELETE-Policy nie
 * getroffen und auth.users nie angefasst - der Nutzer konnte sich danach
 * wieder anmelden, obwohl ihm "unwiderruflich geloescht" zugesagt worden war.
 */
export function DeclineDialog({ consentTypes, state, source, onCancel }: DeclineDialogProps) {
  const { t } = useTranslation(['consent', 'common']);
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const subscription = useSubscriptionNotice();

  const [confirmText, setConfirmText] = useState('');
  const [acknowledgedSubscription, setAcknowledgedSubscription] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expectedConfirmText = t('consent:decline.confirmText');

  // Widerruf, wenn zuvor erteilt - sonst Ablehnung. Der Unterschied steht im
  // Protokoll und im Titel des Dialogs.
  const isWithdrawal = consentTypes.some(
    (type) => state.latest[type]?.decision === 'granted'
  );

  // "Kein Loeschvorgang ohne diesen Hinweis": bei aktivem eigenen Abo muss
  // der Nutzer bestaetigt haben, dass er es selbst kuendigen muss.
  const subscriptionBlocks =
    subscription.hasOwnSubscription && !acknowledgedSubscription;
  const canConfirm =
    confirmText === expectedConfirmText && !subscriptionBlocks && !isDeleting;

  const subscriptionText = () => {
    switch (subscription.channel) {
      case 'apple':
        return t('consent:decline.subscription.apple');
      case 'google':
        return t('consent:decline.subscription.google');
      case 'stripe':
        return t('consent:decline.subscription.stripe');
      default:
        return t('consent:decline.subscription.unknown');
    }
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setIsDeleting(true);
    setError(null);

    try {
      // Erst protokollieren, dann loeschen.
      for (const consentType of consentTypes) {
        await recordConsent({
          consentType,
          decision: state.latest[consentType]?.decision === 'granted' ? 'withdrawn' : 'declined',
          version: state.versions[consentType],
          source,
        });
      }

      await deleteOwnAccount();

      // Nach dem Loeschen von auth.users ist die Session ungueltig; ein
      // fehlschlagender signOut darf den Ablauf nicht aufhalten.
      try {
        await signOut();
      } catch (signOutError) {
        console.warn('Sign-out after account deletion failed:', signOutError);
      }

      navigate('/');
    } catch (err) {
      console.error('Consent decline failed:', err);
      setError(t('consent:decline.error'));
      setIsDeleting(false);
    }
  };

  return (
    <ConsentDialog
      icon={<AlertTriangle className="w-5 h-5 text-red-400" />}
      iconClassName="bg-red-500/20"
      title={isWithdrawal ? t('consent:decline.withdrawTitle') : t('consent:decline.title')}
      footer={
        <>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="w-full py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 disabled:bg-red-500/50 flex items-center justify-center gap-2"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('consent:decline.deleting')}
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                {t('consent:decline.confirmButton')}
              </>
            )}
          </button>
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="w-full py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
          >
            {t('consent:decline.cancel')}
          </button>
        </>
      }
    >
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
        <p
          className="text-white/80 text-sm"
          dangerouslySetInnerHTML={{ __html: t('consent:decline.warning') }}
        />
      </div>

      <div className="mb-4">
        <p className="text-white/70 text-sm font-medium mb-2">
          {t('consent:decline.includesTitle')}
        </p>
        <ul className="text-white/60 text-sm space-y-1 ml-4 list-disc">
          <li>{t('consent:decline.deleteItem1')}</li>
          <li>{t('consent:decline.deleteItem2')}</li>
          <li>{t('consent:decline.deleteItem3')}</li>
          <li>{t('consent:decline.deleteItem4')}</li>
          <li>{t('consent:decline.deleteItem5')}</li>
        </ul>
      </div>

      {subscription.hasOwnSubscription && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-yellow-300 font-medium mb-1">
                {t('consent:decline.subscription.title')}
              </p>
              <p className="text-white/70 text-sm">
                {subscription.paidUntil
                  ? t('consent:decline.subscription.remaining', {
                      date: new Date(subscription.paidUntil).toLocaleDateString('de-DE'),
                    })
                  : t('consent:decline.subscription.remainingUnknown')}
              </p>
              <p className="text-white/70 text-sm mt-2">{subscriptionText()}</p>

              {subscription.canManageInApp && (
                <button
                  onClick={() => navigate('/subscription')}
                  className="mt-2 text-sm text-yellow-300 underline hover:text-yellow-200 flex items-center gap-1"
                >
                  {t('consent:decline.subscription.manage')}
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              )}

              <label className="flex items-start gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledgedSubscription}
                  disabled={isDeleting}
                  onChange={(e) => setAcknowledgedSubscription(e.target.checked)}
                  className="mt-0.5 w-4 h-4 flex-shrink-0 rounded border-white/30 bg-white/10 text-pp-orange focus:ring-pp-orange"
                />
                <span className="text-white/80 text-xs">
                  {t('consent:decline.subscription.acknowledge')}
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      <div>
        <p
          className="text-sm text-white/70 mb-2"
          dangerouslySetInnerHTML={{
            __html: t('consent:decline.confirmPrompt', { confirmText: expectedConfirmText }),
          }}
        />
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
          placeholder={expectedConfirmText}
          disabled={isDeleting}
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
        />
      </div>

      {error && (
        <div className="bg-red-500/20 text-red-300 text-sm p-3 rounded-[10px] mt-4">{error}</div>
      )}
    </ConsentDialog>
  );
}
